import React, { FC, useState, useEffect } from 'react'
import clsx from 'clsx'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import { useField } from 'formik'

import { CloseCircleFilled, InfoCircleFilled } from '@ant-design/icons'
import { Check } from 'react-feather'

import { Button as AntdButton, PopOver, Drawer } from '@pepper/ui'
import Button from '../button/Button'

import styles from './_.module.css'

interface FieldProps {
  name: string
  error?: boolean
  label?: React.ReactNode | string
  helperText?: React.ReactNode | string
  className?: string
  size?: SizeType
  showError?: boolean
  onBlur?: React.EventHandler<React.FocusEvent>
  helperErrorText?: string
  required?: boolean
  style?: React.CSSProperties
  hasHoverEffect?: boolean
}

interface ColorBlockOption {
  title: string
  id: string
  image: string
  details?: string
  multiSelect?: boolean
  disabled?: boolean
  pageSize?: number
  options: SubTemplateOptions[]
}

interface ColorBlockSelectProps extends FieldProps {
  pageSize?: number
  showBrowseMoreLink?: boolean
  popoverInfo?: string
  options: ColorBlockOption[]
  onSelect?: (selectedOption: number | string) => void
  subName: string
  disabled?: boolean
}

export const DesignTemplate: FC<ColorBlockSelectProps> = ({
  showError = true,
  label,
  required,
  showBrowseMoreLink,
  options,
  name,
  subName,
  pageSize = 10,
  onSelect,
  popoverInfo,
  disabled = false,
}) => {
  const [showSubset, toggleSubSet] = useState(false)
  const [field, meta, helpers] = useField(name)
  const [subField, subMeta, subHelpers] = useField(subName)
  const [totalItemsToShow, setTotalItemsToShow] = useState<number>(pageSize)
  const subValueSet = options.find(optionItem => optionItem.id === field.value)
  useEffect(() => {
    if (subMeta.error) {
      toggleSubSet(true)
    }
  }, [subMeta.error])

  const getOptions = (
    templateOptions: any[],
    fieldData: any,
    helpersTag: any,
    subHelper: boolean
  ) => {
    return (
      <div
        className={clsx(styles.ColourBlocksSelection, {
          [styles.subsetWrap]: subHelper,
        })}
        data-cy={name}
      >
        {templateOptions.map(option => {
          const idExists =
            subHelper && fieldData.value
              ? fieldData.value.includes(option.id)
              : fieldData.value === option.id
          return (
            <div key={option.id}>
              <label
                className={clsx(
                  styles.colorBlock,
                  idExists && styles.colourBlockActive,
                  {
                    [styles.disabled]: option.disabled,
                  }
                )}
                {...(fieldData.value === option.id && {
                  'data-cy': `${name}-active`,
                })}
              >
                <img
                  className={clsx(styles.iconWrap, {
                    [styles.subIconWrap]: subHelper,
                  })}
                  src={option.image ? option.image : option.icon}
                  alt={option.title}
                />
                {idExists && subHelper && <div className={styles.overlay} />}
                {idExists && subHelper && (
                  <div className={styles.checkboxWrap}>
                    <Check className={styles.checkboxIcon} />
                  </div>
                )}
                {!subHelper && (
                  <div className={styles.designTitle}>{option.title}</div>
                )}
                <input
                  className={styles.visiblityHidden}
                  {...fieldData}
                  onChange={(): void => {
                    if (typeof onSelect === 'function') onSelect(option.id)
                    if (subHelper) {
                      if (!fieldData.value) {
                        helpersTag.setValue([option.id])
                      } else {
                        const findInd = fieldData.value.indexOf(option.id)
                        const subValueList = [...fieldData.value]
                        findInd > -1
                          ? subValueList.splice(findInd, 1)
                          : subValueList.push(option.id)
                        helpersTag.setValue(subValueList)
                      }
                    } else {
                      helpersTag.setValue(option.id)
                      subHelpers.setValue([])
                      toggleSubSet(true)
                    }
                  }}
                  type={subHelper ? 'checkbox' : 'radio'}
                  value={option.id}
                  disabled={option.disabled || disabled}
                />
              </label>
            </div>
          )
        })}
      </div>
    )
  }

  const subsetSelected = subField.value?.length || 0

  return (
    <div>
      <div>
        <label>
          <span className={styles.labelText}>
            {label}
            {required && <span className={styles.requiredText}>*</span>}
            {popoverInfo && (
              <PopOver content={popoverInfo}>
                <InfoCircleFilled className={styles.infoIcon} />
              </PopOver>
            )}
          </span>
        </label>
        {getOptions(options, field, helpers, false)}
        {meta.touched && meta.error ? (
          <div className={styles.error}>
            <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
          </div>
        ) : null}
        {!meta.error && subMeta.touched && subMeta.error ? (
          <div className={clsx(styles.error, styles.subError)}>
            <CloseCircleFilled className={styles.errorIcon} />
            {subMeta.error}
          </div>
        ) : null}
        {showBrowseMoreLink ? (
          <AntdButton
            className={styles.blockSelect__button}
            onClick={() =>
              setTotalItemsToShow(p => {
                if (options.length <= totalItemsToShow) return pageSize
                else return p + pageSize
              })
            }
            size={'small'}
            type="link"
            disabled={disabled}
          >
            {`Browse ${options.length <= totalItemsToShow ? 'Less' : 'More'}`}
          </AntdButton>
        ) : null}
      </div>

      {field.value && subValueSet && !showSubset && (
        <Button
          disabled={disabled}
          text={
            subsetSelected === 0
              ? 'Pick Styles'
              : `You have selected ${subsetSelected} style${
                  subsetSelected > 1 ? 's' : ''
                }.`
          }
          hideIcon={subsetSelected !== 0}
          update={() => toggleSubSet(true)}
        />
      )}

      {field.value && subValueSet && showSubset && (
        <Drawer
          className={styles.subsetDrawer}
          title={subValueSet.details}
          placement="right"
          closable={true}
          visible={field.value && subValueSet}
          key="sub_set_drawer"
          width={718}
          maskClosable={true}
          onClose={() => {
            toggleSubSet(false)
          }}
          footer={
            <div
              role="none"
              onClick={() => toggleSubSet(false)}
              className={styles.drawerFooter}
            >
              Confirm Selection
            </div>
          }
        >
          <>
            {subMeta.touched && subMeta.error ? (
              <div className={clsx(styles.error, styles.subError)}>
                <CloseCircleFilled className={styles.errorIcon} />
                {subMeta.error}
              </div>
            ) : null}
            {getOptions(subValueSet.options, subField, subHelpers, true)}
          </>
        </Drawer>
      )}
    </div>
  )
}

export default DesignTemplate
